import {MODALS_IDS} from '@Components/modal-container';
import {openPanelInModal} from '@Components/modal-container/modal-container-reducer';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import {GA4EventName, GA4EventParamName} from '@Libraries/ga-events';
import {TicketingComingSoonReasonParam} from '@Libraries/ticketing-ga4.types';
import {isUserPremium, isUserTrial} from '@Libraries/user.library';

/**
 * The client-side "may this organiser choose paid ticketing?" verdict — the ONE copy of the rule, shared by
 * the Tickets tile, the footer's step-2 Next, and the wizard's default registration mode.
 *
 * Anonymous users report the PAYG premium level, so `!isUserPremium()` covers anon by design, and the plan
 * is read here rather than passed because it comes from a cookie the AJAX login refreshes.
 *
 * The two flags are passed, not read: the wizard holds them in Redux
 * (useEventWizardIsPaidTicketingFeatureAvailable / useEventWizardIsTicketingCountrySupported) while the
 * library accessors read `window.reactPageProps`, so each surface supplies its own source.
 */
export const isTicketingSelectionEligible = (isPaidTicketingFeatureAvailable: boolean, isTicketingCountrySupported: boolean): boolean => {
  return isPaidTicketingFeatureAvailable && isTicketingCountrySupported && isUserPremium() && !isUserTrial();
};

/**
 * {@link isTicketingSelectionEligible}, and on a refusal the dialog that explains it.
 *
 * A command, not a predicate, which is why it is not named like one — callers get a modal as well as an
 * answer. Coming-soon beats the upsell: the feature flag first, then the country fake door, then the plan.
 */
export const requireTicketingSelection = (isPaidTicketingFeatureAvailable: boolean, isTicketingCountrySupported: boolean): boolean => {
  if (isTicketingSelectionEligible(isPaidTicketingFeatureAvailable, isTicketingCountrySupported)) {
    return true;
  }
  if (!isPaidTicketingFeatureAvailable || !isTicketingCountrySupported) {
    openTicketingComingSoonModal(!isTicketingCountrySupported);
  } else {
    openEventsUpsellDialog(EventUpsellType.TICKETING, undefined);
  }
  return false;
};

/**
 * The non-US fake door for the Tickets tile and the paid-ticketing step gate. Tracks the view with why it opened
 * (`isCountryBlocked` → the geo-demand signal vs a feature-flag block), then opens the dialog.
 */
const openTicketingComingSoonModal = (isCountryBlocked: boolean): void => {
  const reason = isCountryBlocked ? TicketingComingSoonReasonParam.NON_US : TicketingComingSoonReasonParam.FEATURE;
  window.PMW.gtm.trackGA4CustomEvent(GA4EventName.EVENTS_TICKETING_COMING_SOON, {[GA4EventParamName.REASON]: reason});
  const {notifyMeEmail} = window.PMW.redux.store.getState().eventWizard;
  window.PMW.redux.store.dispatch(
    openPanelInModal({
      modalId: MODALS_IDS.TICKETING_COMING_SOON_MODAL,
      panelProps: {prefillEmail: notifyMeEmail},
    })
  );
};
